import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

type Phase = {
  number: string;
  title: string;
  description: string;
  route: string;
  track: "signal" | "transformation";
};

const phases: Phase[] = [
  {
    number: "01",
    title: "Discovery",
    description: "Diagnose asset utilization, workflows, and operational signals.",
    route: "/services/discovery",
    track: "signal",
  },
  {
    number: "02",
    title: "Experimentation",
    description: "Verify feasibility and cost-effectiveness in labs, simulations, and pilots before investing.",
    route: "/services/solution-design",
    track: "signal",
  },
  {
    number: "03",
    title: "Asset Financing",
    description: "Unlock liquidity and structure asset-based funding or EaaS capital models.",
    route: "/services/asset-financing",
    track: "transformation",
  },
  {
    number: "04",
    title: "Implementation & Governance",
    description: "Deploy equipment, enforce SLA metrics, and sustain auditable performance.",
    route: "/services/performance-management",
    track: "transformation",
  },
];

const OurWorkflow = () => {
  return (
   <section id="workflow" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center rounded-full bg-accent/10 border border-accent/20 px-3 py-1 mb-4">
            <span className="text-xs font-semibold tracking-[0.2em] text-accent uppercase">How It Works</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">Four phases. Two macro-tracks.</h2>
          <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover how our integrated delivery system turns insight into measurable industrial performance.
          </p>
        </div>

        {/* Macro-track headers */}
        <div className="hidden md:grid grid-cols-2 gap-6 mb-4">
          <div className="rounded-xl bg-foreground/95 text-background px-6 py-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest opacity-70">
              Macro-track
            </p>
            <p className="text-lg font-bold">Advisory</p>
            <p className="text-xs opacity-70">Insight &amp; Strategy · Diagnose → Experiment</p>
          </div>
          <div className="rounded-xl bg-primary text-primary-foreground px-6 py-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest opacity-80">
              Macro-track
            </p>
            <p className="text-lg font-bold">Capital &amp; Execution</p>
            <p className="text-xs opacity-90">Financing &amp; Delivery · Fund → Govern</p>
          </div>
        </div>

        {/* Phase grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 lg:gap-6">
          {phases.map((phase, idx) => {
            const isSignal = phase.track === "signal";
            const showArrow = idx < phases.length - 1;
            return (
              <div key={phase.number} className="relative">
                <Link
                  to={phase.route}
                  className={`group block h-full rounded-xl p-6 border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                    isSignal
                      ? "bg-foreground/95 text-background border-foreground/20 hover:bg-foreground"
                      : "bg-primary text-primary-foreground border-primary/30 hover:brightness-110"
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-3xl font-bold opacity-80">
                      {phase.number}
                    </span>
                    <ArrowRight className="h-5 w-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                  <p className="text-sm opacity-80 leading-relaxed">
                    {phase.description}
                  </p>
                </Link>

                {/* Decorative arrow between cards (desktop only) */}
                {showArrow && (
                  <div className="hidden md:flex absolute top-1/2 -right-3 lg:-right-4 -translate-y-1/2 z-10 items-center justify-center w-6 h-6 rounded-full bg-background border border-border shadow-sm">
                    <ArrowRight className="h-3 w-3 text-foreground" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile macro-track labels */}
        <div className="md:hidden mt-6 grid grid-cols-2 gap-3 text-center text-xs">
          <div className="rounded-lg bg-foreground/95 text-background py-2 px-3">
            <p className="font-bold">Advisory</p>
            <p className="opacity-70">Phases 01–02</p>
          </div>
          <div className="rounded-lg bg-primary text-primary-foreground py-2 px-3">
            <p className="font-bold">Capital &amp; Execution</p>
            <p className="opacity-90">Phases 03–04</p>
          </div>
        </div>

        {/* Outcome footer */}
        <div className="mt-10 rounded-xl bg-foreground/95 text-background px-6 py-5 text-center">
          <p className="text-base md:text-lg">
            <span className="font-bold text-primary">Outcome:</span>{" "}
            Measurable industrial performance — scalable, transparent, and auditable.
          </p>
        </div>
      </div>
    </section>
  );
};

export default OurWorkflow;